<div class="space-y-6">
  <!-- Repayment Plan Inputs -->
  <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
    <div class="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 class="flex items-center gap-2 text-lg font-semibold text-[#1E2A78]">
          {{ 'REPAYMENT_PLAN.INPUTS_TITLE' | translate }}

          @if (loadingTermsSummary) {
            <span
              class="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-600"
            >
              <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              {{ 'COMMON.LOADING' | translate }}
            </span>
          }
        </h3>

        <p class="mt-1 text-sm text-gray-500">
          {{ 'REPAYMENT_PLAN.INPUTS_HINT' | translate }}
        </p>
      </div>

      <button
        type="button"
        [disabled]="loadingTermsSummary || loadingExposure"
        (click)="refreshInputs()"
        class="h-11 shrink-0 rounded-xl border border-indigo-200 bg-white px-4 text-sm font-medium text-[#1E2A78] transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ (loadingTermsSummary || loadingExposure ? 'COMMON.LOADING' : 'COMMON.REFRESH') | translate }}
      </button>
    </div>

    @if (termsSummaryErrorMessage) {
      <div class="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {{ termsSummaryErrorMessage }}
      </div>
    }

    @if (exposureErrorMessage) {
      <div class="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {{ exposureErrorMessage }}
      </div>
    }

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">{{ 'REPAYMENT_PLAN.GRACE_PERIOD' | translate }}</p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">{{ gracePeriod }}</p>
      </div>

      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'REPAYMENT_PLAN.NO_OF_INSTALLMENTS' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">{{ numberOfInstallments }}</p>
      </div>

      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">{{ 'REPAYMENT_PLAN.FREQUENCY' | translate }}</p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">{{ selectedFrequencyLabel | translate }}</p>
      </div>

      <div class="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">{{ 'REPAYMENT_PLAN.IN_ARREARS' | translate }}</p>
        <p class="mt-1 text-lg font-bold text-[#1E2A78]">
          {{ (arrearFlg === 1 ? 'COMMON.YES' : 'COMMON.NO') | translate }}
        </p>
      </div>

      <div class="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'REPAYMENT_PLAN.PREVIOUS_MAX_EXPOSURE' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-emerald-800">
          PKR {{ baseExposure | number: '1.0-2' }}
        </p>
      </div>

      <div class="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'REPAYMENT_PLAN.TOTAL_PROPOSED_DISBURSEMENT' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-emerald-800">
          PKR {{ totalDisbursementAmount | number: '1.0-2' }}
        </p>
      </div>

      <div class="rounded-xl border border-amber-100 bg-amber-50 p-4 sm:col-span-2">
        <p class="text-xs font-semibold uppercase text-slate-500">
          {{ 'REPAYMENT_PLAN.PROPOSED_MAX_EXPOSURE' | translate }}
        </p>
        <p class="mt-1 text-lg font-bold text-amber-800">
          PKR {{ proposedMaxExposure | number: '1.0-2' }}
        </p>
      </div>
    </div>
  </div>

  <!-- Disbursements -->
  <div
    class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
  >
    <div
      class="flex flex-col gap-4 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h3
          class="flex items-center gap-2 text-lg font-semibold text-[#1E2A78]"
        >
          {{ 'REPAYMENT_PLAN.PROPOSED_DISBURSEMENTS' | translate }}

          <span
            *ngIf="loadingDisbursements"
            class="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-600"
          >
            <svg
              class="h-3.5 w-3.5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            {{ 'REPAYMENT_PLAN.LOADING_SAVED_DISBURSEMENTS' | translate }}
          </span>
        </h3>

        <p class="mt-1 text-sm text-gray-500">
          {{ 'REPAYMENT_PLAN.DISBURSEMENTS_HINT' | translate }}
          <span
            *ngIf="!loadingDisbursements && savedDisbursementCount > 0"
            class="font-medium text-slate-600"
          >
            {{ 'REPAYMENT_PLAN.ROWS_SAVED' | translate: { saved: savedDisbursementCount, total: disbursements.length } }}
          </span>
        </p>
      </div>

      <button
        *ngIf="editable"
        type="button"
        [disabled]="loadingDisbursements || !availableTermOptionsForDisbursement.length"
        (click)="addDisbursement()"
        class="rounded-xl bg-[#1E2A78] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ 'REPAYMENT_PLAN.ADD_DISBURSEMENT' | translate }}
      </button>
    </div>

    <div *ngIf="loadingDisbursements" class="overflow-x-auto">
      <table class="w-full min-w-[820px]">
        <thead>
          <tr class="bg-gray-50">
            <th class="w-20 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700">{{ 'REPAYMENT_PLAN.S_NO' | translate }}</th>
            <th class="border-b border-gray-200 px-5 py-3 text-start text-sm font-semibold text-gray-700">{{ 'REPAYMENT_PLAN.YEAR_MONTH' | translate }}</th>
            <th class="border-b border-gray-200 px-5 py-3 text-end text-sm font-semibold text-gray-700">{{ 'REPAYMENT_PLAN.PROPOSED_AMOUNT' | translate }}</th>
            <th class="w-28 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700">{{ 'COMMON.STATUS' | translate }}</th>
            <th *ngIf="editable" class="w-28 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700">{{ 'COMMON.ACTION' | translate }}</th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="let placeholder of [1, 2, 3]"
            class="animate-pulse border-b border-gray-100 last:border-b-0"
          >
            <td class="px-5 py-4">
              <div class="mx-auto h-4 w-6 rounded bg-gray-200"></div>
            </td>
            <td class="px-5 py-4">
              <div class="h-9 w-full rounded-lg bg-gray-100"></div>
            </td>
            <td class="px-5 py-4">
              <div class="ms-auto h-9 w-full rounded-lg bg-gray-100"></div>
            </td>
            <td class="px-5 py-4">
              <div class="mx-auto h-5 w-14 rounded-full bg-gray-100"></div>
            </td>
            <td *ngIf="editable" class="px-5 py-4"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <p
      *ngIf="!loadingDisbursements && groupedDisplayDisbursements.length === 0"
      class="px-6 py-5 text-sm text-gray-500"
    >
      {{ 'REPAYMENT_PLAN.NO_DISBURSEMENTS' | translate }}
    </p>

    <div
      *ngFor="let group of groupedDisplayDisbursements; trackBy: trackByDisbursementGroup"
      class="border-t border-gray-200 first:border-t-0"
    >
      <div class="flex items-center justify-between gap-3 px-6 pt-5">
        <h4 class="text-base font-semibold text-[#1E2A78]">
          {{ group.title }}
        </h4>

        <button
          *ngIf="editable"
          type="button"
          [title]="'REPAYMENT_PLAN.ADD_ROW' | translate"
          (click)="addDisbursementRow(group.termRefCdSeq)"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1E2A78] transition hover:bg-[#16205e]"
        >
          <img src="images/plus.png" class="h-4 w-4" [alt]="'REPAYMENT_PLAN.ADD_ROW' | translate" />
        </button>
      </div>

      <div class="overflow-x-auto px-6 pb-6 pt-3">
        <table class="w-full min-w-[820px]">
          <thead>
            <tr class="bg-gray-50">
              <th
                class="w-20 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700"
              >
                {{ 'REPAYMENT_PLAN.S_NO' | translate }}
              </th>

              <th
                class="border-b border-gray-200 px-5 py-3 text-start text-sm font-semibold text-gray-700"
              >
                {{ 'REPAYMENT_PLAN.YEAR_MONTH' | translate }}
              </th>

              <th
                class="border-b border-gray-200 px-5 py-3 text-end text-sm font-semibold text-gray-700"
              >
                {{ 'REPAYMENT_PLAN.PROPOSED_AMOUNT' | translate }}
              </th>

              <th
                class="w-28 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700"
              >
                {{ 'COMMON.STATUS' | translate }}
              </th>

              <th
                *ngIf="editable"
                class="w-28 border-b border-gray-200 px-5 py-3 text-center text-sm font-semibold text-gray-700"
              >
                {{ 'COMMON.ACTION' | translate }}
              </th>
            </tr>
          </thead>

          <tbody>
            @for (entry of group.rows; track entry.index) {
              <tr class="border-b border-gray-100 last:border-b-0">
                <td
                  class="px-5 py-4 text-center align-top font-medium text-gray-700"
                >
                  {{ entry.number }}
                </td>

                <td class="px-5 py-4 align-top">
                  <input
                    type="month"
                    [(ngModel)]="entry.row.schMth"
                    [name]="
                      'disbursementMonth_' +
                      entry.index
                    "
                    [disabled]="!editable || isLockedFirstSlot(entry.row)"
                    [attr.min]="getDisbursementStartMonth(entry.row.termRefCdSeq) || null"
                    [attr.max]="getDisbursementEndMonth(entry.row.termRefCdSeq) || null"
                    (keydown)="blockManualDateEntry($event)"
                    (paste)="$event.preventDefault()"
                    class="h-11 w-full rounded-lg border px-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                    [class.border-gray-300]="!isOutsideDisbursementWindow(entry.row)"
                    [class.border-red-500]="isOutsideDisbursementWindow(entry.row)"
                  />

                  @if (formatMonthLabel(entry.row.schMth)) {
                    <p class="mt-1 ps-1 text-xs text-gray-400">
                      {{ formatMonthLabel(entry.row.schMth) }}
                      @if (editable && isLockedFirstSlot(entry.row)) {
                        · {{ 'REPAYMENT_PLAN.FIRST_SLOT_LOCKED' | translate }}
                      }
                    </p>
                  }

                  @if (isBeforeDisbursementStart(entry.row)) {
                    <p class="mt-1 ps-1 text-xs text-red-600">
                      {{ 'REPAYMENT_PLAN.MONTH_BEFORE_START' | translate: { startMonth: formatMonthLabel(getDisbursementStartMonth(entry.row.termRefCdSeq)) } }}
                    </p>
                  }

                  @if (isAfterDisbursementEnd(entry.row)) {
                    <p class="mt-1 ps-1 text-xs text-red-600">
                      {{ 'REPAYMENT_PLAN.MONTH_AFTER_END' | translate: { endMonth: formatMonthLabel(getDisbursementEndMonth(entry.row.termRefCdSeq)) } }}
                    </p>
                  }
                </td>

                <td class="px-5 py-4 align-top">
                  <div class="relative">
                    <span
                      class="pointer-events-none absolute inset-y-0 start-3 flex items-center text-xs font-semibold text-gray-400"
                    >
                      PKR
                    </span>

                    <input
                      type="text"
                      appAmountInput
                      [(ngModel)]="
                        entry.row.prpsdDsbmtAmt
                      "
                      [name]="
                        'disbursementAmount_' +
                        entry.index
                      "
                      [disabled]="!editable"
                      [placeholder]="'REPAYMENT_PLAN.ENTER_AMOUNT' | translate"
                      class="h-11 w-full rounded-lg border border-gray-300 py-2 ps-12 pe-3 text-end outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>
                </td>

                <td class="px-5 py-4 text-center align-top">
                  @if (entry.row.prpsdDsbmntSeq !== null && entry.row.prpsdDsbmntSeq !== undefined) {
                    <span
                      class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"
                    >
                      {{ 'REPAYMENT_PLAN.SAVED_SEQ' | translate: { seq: entry.row.prpsdDsbmntSeq } }}
                    </span>
                  } @else {
                    <span
                      class="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700"
                    >
                      {{ 'REPAYMENT_PLAN.NEW' | translate }}
                    </span>
                  }
                </td>

                <td
                  *ngIf="editable"
                  class="px-5 py-4 text-center align-top"
                >
                  <button
                    type="button"
                    (click)="
                      requestRemoveDisbursement(
                        entry.index
                      )
                    "
                    class="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50"
                  >
                    {{ 'COMMON.REMOVE' | translate }}
                  </button>
                </td>
              </tr>
            }
          </tbody>

          <tfoot>
            <tr class="bg-indigo-50">
              <td
                colspan="2"
                class="px-5 py-4 text-end text-sm font-semibold text-[#1E2A78]"
              >
                {{ 'REPAYMENT_PLAN.TOTAL_GROUP' | translate: { title: group.title } }}
              </td>

              <td
                class="px-5 py-4 text-end font-bold text-[#1E2A78]"
              >
                PKR {{
                  group.total
                    | number: '1.0-2'
                }}
              </td>

              <td class="px-5 py-4"></td>

              <td
                *ngIf="editable"
                class="px-5 py-4"
              ></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>

  <!-- Messages -->
  <div
    *ngIf="errorMessage"
    class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
  >
    {{ errorMessage }}
  </div>

  <div
    *ngIf="successMessage"
    class="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
  >
    {{ successMessage }}
  </div>

  <!-- Save Button -->
  <!-- <div
    *ngIf="editable"
    class="flex justify-end"
  >
    <button
      type="button"
      [disabled]="
        saving ||
        loadingExposure
      "
      (click)="saveProposedDisbursements()"
      class="rounded-xl bg-[#1E2A78] px-6 py-3 font-medium text-white transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {{
        saving
          ? 'Saving...'
          : 'Save Repayment Plan'
      }}
    </button>
  </div> -->

  <!-- API Result Summary -->
  <div
    *ngIf="resultSummary.length > 0"
    class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
  >
    <h3
      class="mb-5 text-lg font-semibold text-[#1E2A78]"
    >
      {{ 'REPAYMENT_PLAN.SUMMARY_TITLE' | translate }}
    </h3>

    <div
      class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <div
        *ngFor="
          let item of resultSummary;
          trackBy: trackByIndex
        "
        class="rounded-xl border border-gray-200 bg-gray-50 p-4"
      >
        <p class="text-xs font-medium text-gray-500">
          {{ item.label | translate }}
        </p>

        <p
          class="mt-1 font-semibold text-gray-800"
        >
          {{ item.value }}
        </p>
      </div>
    </div>
  </div>

  <!-- API Result Table -->
  <!-- <div
    *ngIf="resultRows.length > 0"
    class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
  >
    <div
      class="border-b border-gray-200 px-6 py-5"
    >
      <h3
        class="text-lg font-semibold text-[#1E2A78]"
      >
        Calculated Repayment Plan
      </h3>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full min-w-[850px]">
        <thead>
          <tr class="bg-[#1E2A78] text-white">
            <th
              class="w-16 border-e border-indigo-400 px-4 py-3 text-center text-sm font-semibold"
            >
              S#
            </th>

            <th
              *ngFor="
                let column of resultColumns;
                trackBy: trackByColumn
              "
              class="border-e border-indigo-400 px-4 py-3 text-start text-sm font-semibold"
            >
              {{
                formatColumnLabel(
                  column
                )
              }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="
              let resultRow of resultRows;
              let resultIndex = index;
              trackBy: trackByIndex
            "
            class="border-b border-gray-200 last:border-b-0"
          >
            <td
              class="border-e border-gray-200 px-4 py-3 text-center font-medium text-gray-700"
            >
              {{ resultIndex + 1 }}
            </td>

            <td
              *ngFor="
                let column of resultColumns;
                trackBy: trackByColumn
              "
              class="border-e border-gray-200 px-4 py-3 text-sm text-gray-700"
            >
              {{
                formatResultValue(
                  resultRow[column]
                )
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div> -->
</div>

<app-popup
  [(show)]="showDeletePopup"
  title="REPAYMENT_PLAN.REMOVE_DISBURSEMENT"
  type="confirm"
  [message]="deleteMessage"
  confirmText="COMMON.REMOVE"
  cancelText="COMMON.CANCEL"
  (confirmed)="confirmRemoveDisbursement()"
></app-popup>

<!-- ADD DISBURSEMENT POPUP -->
<app-popup
  [show]="showAddDisbursementPopup"
  (showChange)="showAddDisbursementPopup = $event"
  title="REPAYMENT_PLAN.ADD_DISBURSEMENT_TITLE"
  type="custom"
  width="w-full max-w-lg min-w-[300px]"
>
  <div class="space-y-6">
    <div>
      <label class="form-label mb-2 block">
        {{ 'REPAYMENT_PLAN.REPAYMENT_TERM' | translate }}
        <span class="text-red-500">*</span>
      </label>

      <select
        [(ngModel)]="draftTermRefCdSeq"
        name="draftTermRefCdSeq"
        class="form-select w-full"
        [class.form-error]="draftTermError"
      >
        <option [ngValue]="null">{{ 'REPAYMENT_PLAN.SELECT_REPAYMENT_TERM' | translate }}</option>
        <option *ngFor="let option of availableTermOptionsForDisbursement" [ngValue]="option.value">
          {{ option.label | translate }}
        </option>
      </select>

      <p *ngIf="draftTermError" class="form-error-text">
        {{ draftTermError | translate }}
      </p>
    </div>

    <div class="flex flex-col justify-end gap-3 border-t border-gray-100 pt-4 sm:flex-row">
      <button type="button" class="btn-outline" (click)="closeAddDisbursementPopup()">
        {{ 'COMMON.CANCEL' | translate }}
      </button>
      <button type="button" class="btn-primary" (click)="confirmAddDisbursementTerm()">
        {{ 'REPAYMENT_PLAN.ADD_TERM' | translate }}
      </button>
    </div>
  </div>
</app-popup>